import React, { useState } from 'react';
import { ExternalLink, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import './reputation.css';

/*
 * Shared UI for indicator reputation (backend services/reputation.py). A verdict chip plus a
 * popover with the plain reasons and the sources that said so. Advisory only, from public feeds.
 */

export const VERDICT = {
  malicious: { label: 'Malicious', icon: ShieldAlert, cls: 'malicious' },
  suspicious: { label: 'Suspicious', icon: ShieldAlert, cls: 'suspicious' },
  benign: { label: 'Benign', icon: ShieldCheck, cls: 'benign' },
  unknown: { label: 'No data', icon: ShieldQuestion, cls: 'unknown' },
  invalid: { label: '', icon: null, cls: 'none' },
  not_in_scope: { label: '', icon: null, cls: 'none' },
  pending: { label: '…', icon: null, cls: 'unknown' },
};

const SOURCE_LINK = { otx: 'OTX', greynoise: 'GreyNoise', threatfox: 'ThreatFox' };

/** Build a map from a reputation result list keyed by canonical and input value, for easy lookup. */
export function reputationMap(results = []) {
  const m = new Map();
  for (const r of results) {
    if (r.value) m.set(String(r.value).toLowerCase(), r);
    if (r.input) m.set(String(r.input).toLowerCase().replace(/\[\.\]/g, '.').replace(/^h..p/i, 'http'), r);
  }
  return m;
}

export function VerdictChip({ result }) {
  const [open, setOpen] = useState(false);
  if (!result || !VERDICT[result.verdict] || VERDICT[result.verdict].cls === 'none') return null;
  const meta = VERDICT[result.verdict];
  const Icon = meta.icon;
  const sources = Object.entries(result.sources || {})
    .filter(([, v]) => v && v.status === 'ok')
    .map(([k, v]) => ({ name: SOURCE_LINK[k] || k, link: v.link }));
  return (
    <span className="rep-wrap">
      <button type="button" className={`rep-chip rep-chip--${meta.cls}`} onClick={() => setOpen((o) => !o)}
        aria-expanded={open} aria-label={`Reputation: ${meta.label}`} data-testid={`rep-${result.verdict}`}>
        {Icon && <Icon aria-hidden="true" />}{meta.label}
      </button>
      {open && (
        <span className="rep-pop" role="dialog">
          <span className="rep-pop-h"><b>{meta.label}</b>{result.malware?.length ? <span className="rep-mw">{result.malware.slice(0, 4).join(', ')}</span> : null}</span>
          {result.reasons?.length ? (
            <ul className="rep-reasons">{result.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
          ) : <span className="rep-muted">No details.</span>}
          {sources.length > 0 && (
            <span className="rep-sources">
              {sources.map((s) => (s.link
                ? <a key={s.name} href={s.link} target="_blank" rel="noopener noreferrer">{s.name} <ExternalLink aria-hidden="true" /></a>
                : <span key={s.name}>{s.name}</span>))}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

export function sourcesNote(sources) {
  const ready = Object.entries(sources || {}).filter(([, v]) => v === 'ready').map(([k]) => SOURCE_LINK[k] || k);
  const off = Object.entries(sources || {}).filter(([, v]) => v === 'not_configured').map(([k]) => SOURCE_LINK[k] || k);
  let s = ready.length ? `Checked against ${ready.join(', ')}.` : 'No reputation source is configured.';
  if (off.length) s += ` ${off.join(', ')} ${off.length === 1 ? 'is' : 'are'} not configured.`;
  return s;
}
